import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import AdminSidebar from "components/ui/AdminSidebar";
import AdminDownbar from "../../components/ui/AdminDownbar";
import logo from "/src/assets/favicon.png";

const AdminHelpScreen = () => {
  const [activeSection, setActiveSection] = useState('general');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const contentRef = useRef(null);
  
  const helpSections = [
    {
      id: 'general',
      title: 'Información General',
      icon: 'Info',
      content: [
        {
          title: 'Bienvenido al Panel de Administración',
          description: 'Este panel te permite gestionar completamente la librería Lithay desde un solo lugar.',
          items: [
            'Agregar, editar y eliminar libros del catálogo',
            'Organizar libros por categorías y autores',
            'Ver qué libros son más populares',
            'Consultar estadísticas de la librería'
          ]
        },
        {
          title: 'Cómo Navegar en el Panel',
          description: 'Usa el menú de la izquierda para ir a las diferentes secciones:',
          items: [
            'Principal (1°): Página de inicio con información general',
            'Autores (2°): Para agregar información de los escritores',
            'Categorías (3°): Para organizar los libros por temas (ficción, romance, etc.)',
            'Catálogo (4°): Donde puedes agregar, editar y eliminar libros',
            'Estadísticas (5°): Para ver números y reportes de la librería'
          ]
        }
      ]
    },
    {
      id: 'authors',
      title: 'Gestión de Autores',
      icon: 'Feather',
      content: [
        {
          title: 'Cómo Agregar un Autor Nuevo',
          description: 'Para agregar escritores a la librería:',
          items: [
            '1. Ve a la sección "Autores" en el menú de la izquierda (segunda opción)',
            '2. Haz clic en el botón "Nuevo Autor"',
            '3. Escribe el nombre completo del autor',
            '4. Agrega una biografía (información sobre el escritor)',
            '5. Sube una foto del autor si tienes una',
            '6. Haz clic en "Guardar"'
          ]
        },
        {
          title: 'Cómo Editar o Eliminar Autores',
          description: 'Para cambiar información de autores:',
          items: [
            '1. Busca el autor en la lista',
            '2. Haz clic en "Editar" para cambiar su información',
            '3. Modifica el nombre, biografía o foto',
            '4. Haz clic en "Guardar" para confirmar los cambios',
            '5. Para eliminar: haz clic en "Eliminar" y confirma'
          ]
        },
        {
          title: 'Cómo Asignar Autores a los Libros',
          description: 'Para conectar autores con sus libros:',
          items: [
            'Cuando agregues o edites un libro, verás una lista de autores',
            'Selecciona el autor correspondiente de la lista',
            'Solo aparecen los autores que ya has creado',
            'Los usuarios podrán ver la información del autor cuando vean el libro'
          ]
        }
      ]
    },
    {
      id: 'categories',
      title: 'Gestión de Categorías',
      icon: 'Layers',
      content: [
        {
          title: 'Cómo Crear una Categoría Nueva',
          description: 'Para organizar los libros por temas:',
          items: [
            '1. Ve a la sección "Categorías" en el menú de la izquierda (tercera opción)',
            '2. Haz clic en el botón "Nueva Categoría"',
            '3. Escribe el nombre de la categoría (por ejemplo: "Romance", "Ficción")',
            '4. Haz clic en "Guardar"',
            '5. La nueva categoría aparecerá en la lista ordenada alfabéticamente'
          ]
        },
        {
          title: 'Cómo Editar una Categoría',
          description: 'Para cambiar el nombre de una categoría:',
          items: [
            '1. Busca la categoría en la lista',
            '2. Haz clic en el botón "Editar" junto a la categoría',
            '3. Cambia el nombre en el campo de texto',
            '4. Haz clic en "Guardar" para confirmar el cambio'
          ]
        },
        {
          title: 'Cómo Eliminar una Categoría',
          description: 'Para cambiar el nombre de una categoría:',
          items: [
            '1. Haz clic en "Eliminar" junto a la categoría',
            '2. Te aparecerá una ventana preguntando si estás seguro',
            '3. Haz clic en "Eliminar" para confirmar',
            '4. La categoría desaparecerá de la lista',
            '5. El sistema maneja automáticamente las dependencias con libros'
          ]
        },
        {
          title: 'Cómo Asignar Categorías a los Libros',
          description: 'Para organizar los libros por temas:',
          items: [
            'Cuando agregues o edites un libro, verás una lista de categorías',
            'Puedes seleccionar una o varias categorías para cada libro',
            'Por ejemplo: un libro puede ser "Romance" y "Ficción" al mismo tiempo',
            'Los usuarios podrán filtrar libros por estas categorías en la tienda'
          ]
        }
      ]
    },
    {
      id: 'catalog',
      title: 'Gestión de Catálogo',
      icon: 'BookOpen',
      content: [
        {
          title: 'Tipos de Productos Disponibles',
          description: 'El sistema maneja tres tipos de productos diferentes:',
          items: [
            '📚 Libros: Productos tradicionales con autor, categorías y descripción',
            '📖 Biblias: Productos especiales con versión, idioma, formato y opción de forro',
            '🎁 Accesorios: Productos complementarios como marcadores, fundas, etc.'
          ]
        },
        {
          title: 'Cómo Agregar un Libro Nuevo',
          description: 'Pasos para agregar un libro al catálogo:',
          items: [
            '1. Ve a la sección "Catálogo" en el menú de la izquierda (cuarta opción)',
            '2. Haz clic en el botón "Nuevo libro"',
            '3. Escribe el título del libro (es obligatorio)',
            '4. Selecciona el autor de la lista',
            '5. Escoge una o varias categorías',
            '6. Llena el precio, número de páginas, idioma, editorial',
            '7. Escribe una descripción del libro',
            '8. Sube una foto del libro',
            '9. Haz clic en "Guardar" para agregar el libro'
          ]
        },
        {
          title: 'Cómo Agregar una Biblia Nueva',
          description: 'Las biblias tienen campos especiales diferentes a los libros:',
          items: [
            '1. Ve a la sección "Catálogo" y haz clic en "Nueva Biblia"',
            '2. Escribe el título de la biblia',
            '3. Ingresa el precio base de la biblia',
            '4. Selecciona la versión (RVR1960, NVI, etc.)',
            '5. Escoge el idioma (Español, Inglés, etc.)',
            '6. Define el formato (Tapa Dura, Tapa Blanda, Digital)',
            '7. Marca "Tiene forro" si la biblia incluye forro opcional',
            '8. Si tiene forro: ingresa el precio del forro (se calculará automáticamente el total)',
            '9. Agrega páginas, descripción e imágenes',
            '10. Haz clic en "Guardar" para agregar la biblia'
          ]
        },
        {
          title: 'Sistema de Precios con Forro para Biblias',
          description: 'Las biblias pueden tener un sistema de precios especial:',
          items: [
            '• Precio base: El costo de la biblia sin forro',
            '• Precio del forro: Costo adicional por el forro (opcional)',
            '• Total con forro: Se calcula automáticamente (precio base + precio del forro)',
            '• En la tienda: Los usuarios ven ambos precios y pueden elegir',
            '• Si no tiene forro: Solo se muestra el precio normal'
          ]
        },
        {
          title: 'Cómo Agregar un Accesorio Nuevo',
          description: 'Los accesorios son productos complementarios:',
          items: [
            '1. Ve a la sección "Catálogo" y haz clic en "Nuevo Accesorio"',
            '2. Escribe el nombre del accesorio',
            '3. Ingresa el precio del producto',
            '4. Selecciona las categorías apropiadas',
            '5. Agrega una descripción detallada',
            '6. Especifica características especiales o materiales',
            '7. Indica la cantidad en stock disponible',
            '8. Sube imágenes del producto',
            '9. Haz clic en "Guardar" para agregar el accesorio'
          ]
        },
        {
          title: 'Cómo Editar Productos',
          description: 'Para cambiar información de cualquier producto:',
          items: [
            '1. Busca el producto en la lista del catálogo',
            '2. Haz clic en el botón "Editar" del producto que quieres cambiar',
            '3. El formulario se adaptará al tipo de producto (libro, biblia o accesorio)',
            '4. Cambia la información que necesites',
            '5. Para biblias: puedes activar/desactivar el forro y ajustar precios',
            '6. Haz clic en "Actualizar" para guardar los cambios'
          ]
        },
        {
          title: 'Navegación y Filtros en el Catálogo',
          description: 'El catálogo incluye herramientas para organizar productos:',
          items: [
            '• Barra de búsqueda: Busca por título, autor o descripción',
            '• Filtros por tipo: Muestra solo libros, biblias o accesorios',
            '• Ordenamiento: Por título, precio, fecha de creación, etc.',
            '• Tarjetas especializadas: Cada tipo de producto muestra información relevante',
            '• Badges de colores: Azul para libros, verde para biblias, morado para accesorios'
          ]
        },
        {
          title: 'Cómo Eliminar Productos',
          description: '⚠️ CUIDADO: Esto borra el producto completamente:',
          items: [
            '1. Busca el producto que quieres eliminar',
            '2. Haz clic en "Editar" para abrir el formulario',
            '3. Haz clic en el botón "Eliminar" (es rojo)',
            '4. Te aparecerá una ventana pidiendo confirmación',
            '5. Escribe el nombre exacto del producto para confirmar',
            '6. Haz clic en "Eliminar" para borrarlo para siempre',
            '7. El sistema maneja automáticamente las dependencias (categorías, autores)'
          ]
        },
        {
          title: 'Exportar Catálogo a PDF',
          description: 'Puedes generar un catálogo profesional en PDF:',
          items: [
            '1. En la pantalla del catálogo, busca el botón "Exportar Catálogo"',
            '2. Haz clic para iniciar la generación del PDF',
            '3. Se abrirá una vista previa del catálogo completo',
            '4. El PDF incluye: portada personalizada, productos por secciones, contraportada',
            '5. Revisa el contenido en la vista previa',
            '6. Haz clic en "Descargar" para obtener el archivo PDF',
            '7. El archivo se nombra automáticamente con la fecha actual'
          ]
        },
        {
          title: 'Sistema de Favoritos',
          description: 'Cómo funciona cuando los usuarios marcan libros como favoritos:',
          items: [
            'Cada persona puede marcar libros como favoritos desde su teléfono o computadora',
            'El sistema cuenta cuántas personas han marcado cada libro como favorito',
            'Puedes ver qué libros son más populares en las estadísticas',
            'Los números se actualizan automáticamente'
          ]
        }
      ]
    },
    {
      id: 'stats',
      title: 'Estadísticas y Reportes',
      icon: 'BarChart3',
      content: [
        {
          title: 'Tipos de Reportes Disponibles',
          description: 'El panel incluye varios tipos de reportes:',
          items: [
            'Ventas por período',
            'Libros más populares',
            'Categorías con mayor demanda',
            'Estadísticas de inventario',
            'Análisis de precios y descuentos'
          ]
        },
        {
          title: 'Exportar Reportes',
          description: 'Para exportar los reportes:',
          items: [
            'Selecciona el tipo de reporte',
            'Define el período de tiempo',
            'Haz clic en "Exportar"',
            'Elige el formato (PDF, Excel)',
            'Descarga el archivo'
          ]
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Solución de Problemas',
      icon: 'AlertTriangle',
      content: [
        {
          title: 'Problemas Comunes',
          description: 'Soluciones para problemas frecuentes:',
          items: [
            'Si no puedes iniciar sesión, verifica tus credenciales',
            'Si las imágenes no se cargan, verifica el formato (JPG, PNG)',
            'Si los cambios no se guardan, verifica tu conexión a internet',
            'Si el panel no responde, recarga la página'
          ]
        },
        {
          title: 'Contacto de Soporte',
          description: 'Si necesitas ayuda adicional, contacta a:',
          items: [
            'Nombre: Soporte Angello',
            'Teléfono: 0412 2182303',
            'Email: angelloxon@gmail.com',
            'Incluye capturas de pantalla al reportar problemas'
          ]
        }
      ]
    },
    {
      id: 'password',
      title: 'Cambiar Contraseña',
      icon: 'Key',
      content: [
        {
          title: 'Acceso al Cambio de Contraseña',
          description: 'Puedes cambiar tu contraseña desde dos ubicaciones:',
          items: [
            '• Menú Principal: Haz clic en tu nombre (esquina superior derecha) → "Cambiar Contraseña"',
            '• Menú Móvil: En la barra inferior, busca el botón "Contraseña" con ícono de llave'
          ]
        },
        {
          title: 'Proceso de Cambio',
          description: 'Una vez que accedas al modal de cambio de contraseña:',
          items: [
            '1. Ingresa tu nueva contraseña (mínimo 6 caracteres)',
            '2. Confirma la nueva contraseña escribiéndola nuevamente',
            '3. Usa el ícono del ojo para mostrar/ocultar las contraseñas',
            '4. Haz clic en "Continuar" para proceder',
            '5. Confirma el cambio haciendo clic en "Sí, Cambiar Contraseña"',
            '6. Verás un mensaje de éxito cuando se actualice correctamente'
          ]
        },
        {
          title: 'Características del Modal',
          description: 'El nuevo sistema de cambio de contraseña incluye:',
          items: [
            '• No requiere contraseña actual (cambio directo)',
            '• Iconos de ojo para mostrar/ocultar contraseñas',
            '• Validación en tiempo real',
            '• Confirmación antes del cambio',
            '• Actualización inmediata en el sistema'
          ]
        }
      ]
    }
  ];

  const currentSection = helpSections.find(section => section.id === activeSection);

  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    
    // En móviles, hacer scroll al contenido
    if (window.innerWidth < 1024 && contentRef.current) {
      setTimeout(() => {
        contentRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-slate-50 pb-[40px] lg:pb-0">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block">
          <AdminSidebar
            isCollapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            userName="Administrador"
          />
        </div>
        {/* Mobile Bottom Bar */}
        <div className="lg:hidden">
          <AdminDownbar />
        </div>
        {/* Main Content */}
        <div className={`pb-20 lg:pb-0 ${sidebarCollapsed ? "lg:pl-20" : "lg:pl-64"}`}>
          {/* Header */}
        <header className="bg-card border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 gap-4 sm:gap-0">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <img src={logo} alt="Logo" className="w-8 h-8 shrink-0" />
                <div className="min-w-0">
                  <h1 className="text-xl font-heading font-semibold text-card-foreground truncate">
                    Centro de Ayuda
                  </h1>
                  <p className="text-muted-foreground text-xs italic mt-1 leading-snug">
                    “Clama a mí, y yo te responderé...”
                    <span className="block text-[10px] text-muted-foreground">— Jeremías 33:3</span>
                  </p>
                </div>
              </div>
            </div>
            <Link to="/admin-dashboard-screen" className="self-start sm:self-auto">
              <Button variant="outline" size="sm">
                <Icon name="ArrowLeft" size={16} className="mr-2" />
                Volver al Panel
              </Button>
            </Link>
          </div>
        </header>
          {/* Content */}
          <main className="p-4 sm:p-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Sidebar Navigation */}
                <div className="lg:col-span-1">
                  <div className="bg-card rounded-lg border border-border p-4 sticky top-6">
                    <h2 className="text-lg font-semibold text-card-foreground mb-4">
                      Secciones de Ayuda
                    </h2>
                    <nav className="space-y-2">
                      {helpSections.map((section) => (
                        <button
                          key={section.id}
                          onClick={() => handleSectionClick(section.id)}
                          className={`w-full text-left flex items-center space-x-3 px-3 py-2 rounded-md transition-smooth ${
                            activeSection === section.id
                              ? 'bg-primary text-primary-foreground'
                              : 'text-muted-foreground hover:text-card-foreground hover:bg-muted'
                          }`}
                        >
                          <Icon name={section.icon} size={16} />
                          <span className="text-sm font-medium">{section.title}</span>
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>

                {/* Main Content */}
                <div className="lg:col-span-3">
                  <div ref={contentRef} className="bg-card rounded-lg border border-border p-6">
                    {currentSection && (
                      <>
                      <div className="flex items-center space-x-3 mb-6">
                        <Icon name={currentSection.icon} size={24} className="text-primary" />
                        <h2 className="text-2xl font-heading font-semibold text-card-foreground">
                          {currentSection.title}
                        </h2>
                      </div>

                      <div className="space-y-8">
                        {currentSection.content.map((item, index) => (
                          <div key={index} className="border-b border-border pb-6 last:border-b-0">
                            <h3 className="text-lg font-semibold text-card-foreground mb-3">
                              {item.title}
                            </h3>
                            <p className="text-muted-foreground mb-4">
                              {item.description}
                            </p>
                            {item.items && (
                              <ul className="space-y-2">
                                {item.items.map((listItem, itemIndex) => (
                                  <li key={itemIndex} className="flex items-start space-x-2">
                                    <Icon name="Check" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-card-foreground">{listItem}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            
                            {item.subSections && (
                              <div className="mt-4 space-y-3">
                                {item.subSections.map((subItem, subIndex) => (
                                  <div key={subIndex}>
                                    <h4 className="text-md font-semibold text-card-foreground mb-2">
                                      {subItem.title}
                                    </h4>
                                    <p className="text-muted-foreground mb-3">
                                      {subItem.description}
                                    </p>
                                    {subItem.items && (
                                      <ul className="space-y-2">
                                        {subItem.items.map((subListItem, subItemIndex) => (
                                          <li key={subItemIndex} className="flex items-start space-x-2">
                                            <Icon name="Check" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                                            <span className="text-sm text-card-foreground">{subListItem}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Quick Actions */}
                      <div className="mt-6 bg-card rounded-lg border border-border p-6">
                        <h3 className="text-lg font-semibold text-card-foreground mb-4">
                          Acciones Rápidas
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          <Link to="/admin-catalog-management-screen">
                            <Button variant="outline" className="w-full justify-start">
                              <Icon name="BookOpen" size={16} className="mr-2" />
                              Gestionar Catálogo
                            </Button>
                          </Link>
                          <Link to="/admin-categories">
                            <Button variant="outline" className="w-full justify-start">
                              <Icon name="Tag" size={16} className="mr-2" />
                              Administrar Categorías
                            </Button>
                          </Link>
                          <Link to="/admin-reports">
                            <Button variant="outline" className="w-full justify-start">
                              <Icon name="BarChart3" size={16} className="mr-2" />
                              Ver Reportes
                            </Button>
                          </Link>
                        </div>
                      </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default AdminHelpScreen;

